/**
 * Config Service
 * Fetches and caches application configuration from /api/v1/general/configs
 * Uses memory-only caching (no localStorage) for simplicity and code review compliance
 */

import secureApi from '../utils/secureApi';
import { AppConfig, ConfigApiResponse, BannerConfig } from '../types/config';

class ConfigService {
  private static instance: ConfigService;
  private config: AppConfig | null = null;
  private fetchPromise: Promise<AppConfig> | null = null;
  private isInitialized: boolean = false;

  private constructor() {}

  static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  /**
   * Initialize config service - fetches config from API
   * Should be called early in app lifecycle (e.g., App.tsx)
   */
  async initialize(): Promise<AppConfig> {
    if (this.isInitialized && this.config) {
      return this.config;
    }

    // If already fetching, return existing promise
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Fetch fresh config from API
    this.fetchPromise = this.fetchFromAPI();
    
    try {
      this.config = await this.fetchPromise;
      this.isInitialized = true;
      return this.config;
    } catch (error) {
      console.error('Failed to initialize config service:', error);
      // Reset fetch promise on error so we can retry
      this.fetchPromise = null;
      throw error;
    } finally {
      this.fetchPromise = null;
    }
  }

  /**
   * Fetch config from API
   */
  private async fetchFromAPI(): Promise<AppConfig> {
    try {
      const response = await secureApi.get<any>('/api/v1/general/configs');
      
      // Handle different response structures
      let configData: AppConfig;
      
      if ((response as any)?.statusCode === 200 && (response as any)?.data) {
        // Wrapped response: { statusCode, status, message, data: {...} }
        configData = (response as ConfigApiResponse).data;
      } else if ((response as any)?.cdnBaseUrl) {
        // Direct config object
        configData = response as unknown as AppConfig;
      } else {
        throw new Error('Unexpected config API response structure');
      }

      // Validate that cdnBaseUrl exists
      if (!configData.cdnBaseUrl) {
        console.warn('Config API response missing cdnBaseUrl, using fallback');
        configData.cdnBaseUrl = 'https://sfl-admin.s3.ap-south-1.amazonaws.com';
      }

      return configData;
    } catch (error) {
      console.error('Error fetching config from API:', error);
      // Return fallback config if API fails
      return {
        banners: {},
        sports: [],
        cities: [],
        feedbackIssueTags: [],
        feedbackCategories: [],
        contactUsQueries: [],
        supportContact: '',
        supportTeamAvailability: '',
        faq: [],
        referralConfig: {
          firstEnrollmentDiscount: 0,
          inAppPaymentPointValue: 0,
          voucherPointValue: 0,
        },
        cdnBaseUrl: 'https://sfl-admin.s3.ap-south-1.amazonaws.com', // Fallback
      };
    }
  }

  /**
   * Get full config object
   */
  async getConfig(): Promise<AppConfig> {
    if (this.config) {
      return this.config;
    }

    // If not initialized, initialize now
    if (!this.isInitialized) {
      return this.initialize();
    }

    // If config is null but initialized, fetch again
    return this.fetchFromAPI();
  }

  /**
   * Get CDN base URL (synchronous, uses cached config)
   * Returns fallback if config not loaded yet
   */
  getCdnBaseUrl(): string {
    if (this.config?.cdnBaseUrl) {
      return this.config.cdnBaseUrl;
    }
    
    // Fallback during config load
    return 'https://sfl-admin.s3.ap-south-1.amazonaws.com';
  }

  /**
   * Get banners config
   */
  getBanners(): BannerConfig {
    return this.config?.banners || {};
  }

  /**
   * Get sports config
   */
  getSports() {
    return this.config?.sports || [];
  }

  /**
   * Get all config values
   */
  getAllConfig(): AppConfig | null {
    return this.config;
  }

  /**
   * Check if config is loaded
   */
  isConfigLoaded(): boolean {
    return this.config !== null && this.isInitialized;
  }

  /**
   * Force refresh config from API
   */
  async refreshConfig(): Promise<AppConfig> {
    this.isInitialized = false;
    this.config = null;
    return this.initialize();
  }

  /**
   * Clear config (useful for testing or logout scenarios)
   */
  clearConfig(): void {
    this.config = null;
    this.isInitialized = false;
    this.fetchPromise = null;
  }
}

export default ConfigService.getInstance();

